import { useEffect, useRef, useState } from 'react';
import { useDispatch, useLogger, useSelector } from '~Hooks';

interface Request {
  reference: string;
  status: 'PENDING' | 'SUCCESS' | 'ERROR';
  error?: any;
}

interface RefundProps {
  amount: number;
  orderId: string;
  transactionId: string;
  onCompleted: () => void;
  onFail: () => void;
}

/**
 * This is a hook that handles the Zettle refund flow.
 */
export const useZettle = () => {
  const dispatch = useDispatch();
  const logger = useLogger('refunds');
  const [reference, setReference] = useState<string | null>(null);

  const refundRequest = useSelector((state) =>
    state.refundBridgeRequests.find((ref) => ref.reference === reference),
  );

  const requestStatus = useRef<Request | null>(null);

  /* Listening to change */
  useEffect(() => {
    if (!refundRequest || !requestStatus.current) {
      return;
    }

    if (refundRequest.status === 'SUCCESS') {
      logger.info('Refund - Zettle Refund successful on reference: ' + reference, {
        refundRequest,
      });

      requestStatus.current.status = 'SUCCESS';
    }
    if (refundRequest.error) {
      logger.error('Refund - Zettle Refund failed on reference: ' + reference, {
        refundRequest,
        err: refundRequest.error,
      });
    }
  }, [refundRequest]);

  async function refund({ transactionId, orderId, onCompleted, onFail, amount }: RefundProps) {
    if (requestStatus.current) {
      throw new Error('Refund - Zettle Refund already in progress');
    }

    requestStatus.current = {
      reference: transactionId,
      status: 'PENDING',
    };

    logger.info('Refund - Zettle Refund initiated on transactionId: ' + transactionId, {
      refundRequest,
    });

    /* Register the request */
    dispatch({
      type: 'REFUND_BRIDGE_REQUEST',
      orderId: orderId,
      reference: transactionId,
      amount,
    });

    /* Send data to native wrapper */
    window.postMessageBridge({
      refund: {
        id: 'ref',
        reference: transactionId,
        amount,
      },
    });

    setReference(transactionId);

    const id = setInterval(() => {
      if (requestStatus.current?.status === 'SUCCESS') {
        clearInterval(id);
        onCompleted();
      }

      if (requestStatus.current?.status === 'ERROR') {
        clearInterval(id);
        onFail();
      }
    }, 1000);
  }

  return {
    refund,
  };
};
